import { useContext, useEffect, useState } from 'react'
import getConfig from 'next/config'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'

import 'web/lib/string-utils'

import { getContractData } from 'web/lib/contract'
import GawdManifest from 'web/lib/gawd-manifest'
import Gawd, { Power, PowerType } from 'web/lib/gawd'

import styles from 'web/pages/gallery/[id].module.css'

import Layout from 'web/components/Layout'
import RadarChart from 'web/components/RadarChart'
import GawdPlayer from 'web/components/GawdPlayer'
import TamaGawdtchi from 'web/components/TamaGawdtchi'
import NextGawdButton from 'web/components/NextGawdButton'
import PowerIcon from 'web/components/PowerIcon'
import useEnvironment from 'web/hooks/useEnvironment'
import useIsGawdOwner from 'web/hooks/useIsGawdOwner'

const {
  publicRuntimeConfig: { gawdsPerPage },
} = getConfig()

function formatSignifier(format) {
  if (format.spatial === 'lookingglass') {
    return ' (Looking Glass)'
  } else {
    if (format.fileType === 'MP4') {
      if (format.width > 3000) {
        return ' (4K)'
      } else if (format.width === 1920) {
        return ' (Full HD)'
      } else if (format.width === 1080) {
        return ' (HD)'
      }
    }
    if (format.fileType === 'PNG' && format.width === 7200) {
      return ' (Print)'
    }
  }
}

export async function getStaticPaths() {
  const mintedSupply = await GawdManifest.getMintedGawds()

  const gawdsToPregenerate = mintedSupply.slice(-(gawdsPerPage + 1), -1) // it's not "-0" so add 1 more
  const formattedItems = gawdsToPregenerate.map((gawd) => {
    return {
      params: { id: gawd.id().toString() },
    }
  })

  return {
    paths: formattedItems,
    fallback: true,
  }
}

export async function getStaticProps({ params }) {
  // Find minted gawd in the manifest
  let gawdObj = await GawdManifest.findById(params.id)
  const contractData = await getContractData()

  if (!gawdObj) {
    console.warn(`Gawd page: error, Gawd id ${params.id} was not found, returning 404`)
    return { notFound: true }
  }

  // Instantiate the data
  await gawdObj.load()

  // Make sure S3 files are set to public
  if (!process.env.SKIP_ACL_UPDATE) {
    console.log('Updating S3 assets to be public')
    await gawdObj.updateAclToPublic()
  }

  const gawd = gawdObj.toJson()

  console.log(`Gawd page: generating: /gallery/${params.id} - gawd=`, gawd?.name, gawd?.image)

  return {
    props: { gawd, contractData },
    revalidate: 60,
  }
}

interface PostProps {
  gawd: Gawd,
  contractData: any
}

export default function Post({ gawd, contractData }: PostProps) {
  const { isBeta } = useEnvironment()
  const router = useRouter()

  // load data back into the Gawd wrapper class
  gawd = new Gawd(gawd)

  // Check if the connected wallet is the owner
  const isOwner = useIsGawdOwner(gawd)

  // Manage scroll position between gawd pagination
  const [lastScrollPos, setScrollPos] = useState<number>(0)
  const onNextGawdClick = () => {
    setScrollPos(window.scrollY)
  }
  useEffect(() => {
    window.scrollTo(0, lastScrollPos)
  }, [gawd?.id()])

  const tweetMessage = `Behold ${gawd.name()} and cower in its presence. @cryptogawds ${gawd.galleryUrl()}`

  if (router.isFallback) {
    return (
      <Layout initContractData={contractData}>
        <h1 className="text-center mt-12 text-3xl md:text-4xl">Loading data from the void...</h1>
      </Layout>
    )
  }

  return (
    <Layout initContractData={contractData}>
      <NextSeo
        title={`${gawd.name()} - #${gawd.id()}`}
        openGraph={{
          title: `${gawd.name()} #${gawd.id()} ✧ GAWDS`,
          images: [
            {
              url: gawd.getImageBySize(1080),
              alt: gawd.name(),
              height: 1080,
              width: 1080,
              // type: 'image/png',
            },
          ],
        }}
      />

      <div className="animate-fadeIn py-8">
        <div className="text-center">
          {/* Gawd name & cult header */}
          <div className="flex mb-10">
            <div className="flex-grow">
              <h2 className="text-3xl">
                {gawd.name().replace(/_/g, ' ')}
              </h2>
              <div className="mt-3">
                <span className="bg-red-300 text-red-500 font-normal text-l p-1 px-3 rounded-xl">
                  {gawd.cult()}
                </span>
                {isOwner &&
                  <span className="text-gray-500 px-2">
                    owned by you
                  </span>}

              </div>
            </div>
          </div>
          {/* Gawd player & next/prev btns */}
          <div className="flex">
            <div className="md:flex-grow w-1/10 md:w-8 text-right ">
              {isOwner && <NextGawdButton previous={true} curGawd={gawd} onClick={onNextGawdClick} />}
            </div>
            {gawd && gawd?.data && (
              <GawdPlayer
                data={gawd.data}
                video={true}
                toggleModes={true}
                className="w-4/5 pt-4/5 md:w-3/5 md:pt-3/5 z-0 cursor-pointer"
              />
            )}
            <div className="md:flex-grow w-1/10 md:w-8 text-left">
              {isOwner && <NextGawdButton next={true} curGawd={gawd} onClick={onNextGawdClick} />}
            </div>
          </div>

          {/* only show gawchi if globally enabled and user's wallet is connected */}
          {isBeta &&
            <TamaGawdtchi id={gawd.id()} />
          }
        </div>

        <div className="space-y-10 flex flex-col md:flex-row md:gap-4 md:max-w-4xl md:m-auto pt-8">
          <div className="order-last flex-1 md:order-first space-y-4">
            <div className="flex align-center justify-center">
              <RadarChart gawd={gawd} />
            </div>

            <div id="formats" className="space-y-2">
              <h4 className="font-normal text-sm text-gray-500">Formats</h4>

              <ul className="divide-y divide-gray-800 border border-gray-800 rounded-md">
                {gawd.assets().map(
                  (format) =>
                    format.downloadable && (
                      <li key={format.id}>
                        <a href={format.url.toGatewayUrl()} className="no-underline" target="_blank">
                          <div className="px-4 py-4">
                            <div className="flex items-center justify-between font-medium text-white truncate">
                              <div className="flex-shrink">
                                {/* Paperclip */}
                                <svg
                                  width="14"
                                  height="16"
                                  viewBox="0 0 14 16"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M5 2C3.34315 2 2 3.34315 2 5V9C2 11.7614 4.23858 14 7 14C9.76142 14 12 11.7614 12 9V5C12 4.44772 12.4477 4 13 4C13.5523 4 14 4.44772 14 5V9C14 12.866 10.866 16 7 16C3.13401 16 0 12.866 0 9V5C0 2.23858 2.23858 0 5 0C7.76142 0 10 2.23858 10 5V9C10 10.6569 8.65685 12 7 12C5.34315 12 4 10.6569 4 9V5C4 4.44772 4.44772 4 5 4C5.55228 4 6 4.44772 6 5V9C6 9.55228 6.44772 10 7 10C7.55228 10 8 9.55228 8 9V5C8 3.34315 6.65685 2 5 2Z"
                                    fill="#9CA3AF"
                                  />
                                </svg>
                              </div>

                              <div className="flex-1 text-left ml-3">
                                {format.fileType} {formatSignifier(format)}
                              </div>

                              <div className="flex-shrink-0 leading-5 font-semibold text-red-500">Download</div>
                            </div>

                            <div className="flex justify-between items-center text-gray-500">
                              {format.size.width} x {format.size.height}
                              <div className="mt-2 flex items-center text-gray-500 sm:mt-0 sm:ml-6">
                                {Math.round((format.fileSize / 1024 / 1024) * 100) / 100} MB
                              </div>
                            </div>
                          </div>
                        </a>
                      </li>
                    )
                )}
              </ul>
            </div>
          </div>

          <div className="flex-1 space-y-8">
            <div id="menu" className="grid gap-4 md:grid-cols-2">
              <a href={gawd.openseaUrl()} className={styles.secondary} target="_blank">
                View on OpenSea
              </a>

              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetMessage)}`}
                className={styles.secondary}
                target="_blank"
              >
                Tweet
              </a>
            </div>

            <div id="anatomy" className="space-y-2">
              <h4 className="font-normal text-sm text-gray-500">Anatomy</h4>

              <ul className="divide-y divide-gray-800 border border-gray-800 rounded-md">
                {gawd.traits().map((trait, index) => (
                  <li key={index}>
                    <div className="py-3 px-4 md:py-4">
                      <div className="flex items-center justify-between font-medium text-gray-500 truncate">
                        <div className="part-power flex-1 text-left">
                          {trait.trait_type}
                        </div>

                        {trait.set && (
                          <div className="mt-2 flex items-center text-gray-500 sm:mt-0 sm:ml-6 text-xs">
                            {trait.set} Set
                          </div>
                        )}
                      </div>

                      <div className="part-title mt-2 flex justify-between items-center text-white">
                        <div>
                          {trait.trait_type == 'Dominant Power' &&
                            <PowerIcon power={Power.getByName(trait.value)} className="w-5 w-5 inline mr-1 fill-white" />}
                          {trait.value}
                        </div>

                        <div className="mt-2 flex items-center text-red-500 sm:mt-0 sm:ml-6">
                          <PowerIcon power={Power.getByName(trait.power)} className="w-3 w-3 inline mr-1 fill-red-500" /> {trait.power}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  )
}
